import * as THREE from 'three';

export const LUNAR_ASSET_COLORS = {
  surface: '#e5dfd0',
  crater: '#bbb5aa',
  mare: '#343638',
  meteor: '#565957',
  meteorWarm: '#4f504d',
  hotLava: '#ff9a45',
  hotLavaEmissive: '#ff4b1f',
  coolLava: '#2b211d',
  coolLavaEmissive: '#100d0c',
} as const;

type CraterGeometryOptions = {
  radius: number;
  depth: number;
  moonRadius: number;
  seed: number;
  segments?: number;
  rings?: number;
};

/** Shared airless asteroid silhouette used by the story and the sculpting game. */
export function createMeteorRockGeometry(radius = 0.24, detail = 3, seed = 0): THREE.IcosahedronGeometry {
  const geometry = new THREE.IcosahedronGeometry(radius, detail);
  const positions = geometry.getAttribute('position');
  const point = new THREE.Vector3();
  const direction = new THREE.Vector3();
  const colors: number[] = [];
  const craterDirections = [
    new THREE.Vector3(0.72, 0.38, 0.58).normalize(),
    new THREE.Vector3(-0.46, 0.78, 0.42).normalize(),
    new THREE.Vector3(0.16, -0.56, 0.81).normalize(),
  ];

  for (let index = 0; index < positions.count; index += 1) {
    point.fromBufferAttribute(positions, index);
    direction.copy(point).normalize();
    const broadRelief =
      Math.sin(direction.x * 4.9 + direction.y * 2.7 + seed * 0.43) * 0.075
      + Math.sin(direction.y * 7.1 - direction.z * 3.8 - seed * 0.27) * 0.042
      + Math.cos(direction.z * 9.3 + direction.x * 4.1 + seed * 0.19) * 0.026;
    let craterRelief = 0;
    let craterShade = 0;
    for (let craterIndex = 0; craterIndex < craterDirections.length; craterIndex += 1) {
      const dot = direction.dot(craterDirections[craterIndex]);
      const edge = craterIndex === 1 ? 0.91 : 0.935;
      const inside = THREE.MathUtils.smoothstep(dot, edge, 0.995);
      const rim = Math.exp(-Math.pow((dot - edge) / 0.018, 2));
      craterRelief -= inside * (craterIndex === 1 ? 0.085 : 0.06);
      craterRelief += rim * 0.018;
      craterShade = Math.max(craterShade, inside);
    }
    const distortion = 1 + broadRelief + craterRelief;
    point.set(point.x * 1.09, point.y * 0.91, point.z * 1.02).multiplyScalar(distortion);
    positions.setXYZ(index, point.x, point.y, point.z);

    const mineralVariation =
      Math.sin(direction.x * 13.7 + direction.z * 8.1 + seed) * 0.055
      + Math.cos(direction.y * 11.3 - direction.x * 5.6 - seed * 0.7) * 0.035;
    const shade = THREE.MathUtils.clamp(0.91 + mineralVariation - craterShade * 0.19, 0.63, 1.02);
    colors.push(shade * 0.98, shade * 0.965, shade * 0.92);
  }

  positions.needsUpdate = true;
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.computeVertexNormals();
  return geometry;
}

/**
 * Smooth circular impact basin with a narrow raised lip and slight rim
 * irregularity. The profile is shared so story craters match player-made ones.
 */
export function createLunarCraterGeometry({
  radius,
  depth,
  moonRadius,
  seed,
  segments = 48,
  rings = 12,
}: CraterGeometryOptions): THREE.BufferGeometry {
  const surfaceLift = 0.003;
  const positions: number[] = [0, 0, lunarCraterProfile(0, depth) + surfaceLift];
  const centerShade = lunarCraterSurfaceShade(0);
  const colors: number[] = [centerShade, centerShade * 0.975, centerShade * 0.93];
  const indices: number[] = [];

  for (let ring = 1; ring <= rings; ring += 1) {
    const t = ring / rings;
    const shade = lunarCraterSurfaceShade(t);
    for (let segment = 0; segment < segments; segment += 1) {
      const angle = (segment / segments) * Math.PI * 2;
      const rimBlend = THREE.MathUtils.smoothstep(t, 0.7, 1);
      const rimVariation = (
        Math.sin(angle * 5 + seed * 0.83) * 0.009
        + Math.sin(angle * 11 - seed * 0.37) * 0.004
      ) * rimBlend;
      const ringRadius = radius * t * (1 + rimVariation);
      const surfaceCurve = Math.sqrt(Math.max(moonRadius * moonRadius - ringRadius * ringRadius, 0.001)) - moonRadius;
      const relief = lunarCraterProfile(t, depth) + surfaceLift;
      positions.push(Math.cos(angle) * ringRadius, Math.sin(angle) * ringRadius, surfaceCurve + relief);
      colors.push(shade, shade * 0.975, shade * 0.93);
    }
  }

  for (let segment = 0; segment < segments; segment += 1) {
    indices.push(0, 1 + segment, 1 + ((segment + 1) % segments));
  }
  for (let ring = 1; ring < rings; ring += 1) {
    const current = 1 + (ring - 1) * segments;
    const nextRing = current + segments;
    for (let segment = 0; segment < segments; segment += 1) {
      const next = (segment + 1) % segments;
      indices.push(current + segment, nextRing + segment, current + next);
      indices.push(current + next, nextRing + segment, nextRing + next);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

export function createLavaMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: LUNAR_ASSET_COLORS.hotLava,
    emissive: LUNAR_ASSET_COLORS.hotLavaEmissive,
    emissiveIntensity: 2.2,
    roughness: 0.65,
    metalness: 0,
  });
}

type SurfacePatchGeometryOptions = {
  radius: number;
  moonRadius: number;
  seed: number;
  surfaceOffset?: number;
  segments?: number;
  rings?: number;
};

/** Curved irregular lava/mare patch shared by the formation story and game. */
export function createLunarSurfacePatchGeometry({
  radius,
  moonRadius,
  seed,
  surfaceOffset = 0.012,
  segments = 36,
  rings = 4,
}: SurfacePatchGeometryOptions): THREE.BufferGeometry {
  const positions: number[] = [0, 0, surfaceOffset];
  const colors: number[] = [1, 0.99, 0.96];
  const indices: number[] = [];
  const surfaceZ = (x: number, y: number): number =>
    Math.sqrt(Math.max((moonRadius + surfaceOffset) ** 2 - x * x - y * y, 0.001)) - moonRadius;

  for (let ring = 1; ring <= rings; ring += 1) {
    const progress = ring / rings;
    for (let segment = 0; segment < segments; segment += 1) {
      const angle = (segment / segments) * Math.PI * 2;
      const coastVariation =
        Math.sin(angle * 3 + seed * 0.83) * 0.085
        + Math.sin(angle * 5 - seed * 0.47) * 0.045
        + Math.sin(angle * 9 + seed * 1.19) * 0.022;
      const ringRadius = radius * progress * (1 + coastVariation * progress);
      const x = Math.cos(angle) * ringRadius;
      const y = Math.sin(angle) * ringRadius;
      const mottling = 0.94 + Math.sin(angle * 7 + ring * 1.71 + seed) * 0.035;
      positions.push(x, y, surfaceZ(x, y));
      colors.push(mottling, mottling * 0.995, mottling * 0.97);
    }
  }

  for (let segment = 0; segment < segments; segment += 1) {
    indices.push(0, 1 + segment, 1 + ((segment + 1) % segments));
  }
  for (let ring = 1; ring < rings; ring += 1) {
    const current = 1 + (ring - 1) * segments;
    const nextRing = current + segments;
    for (let segment = 0; segment < segments; segment += 1) {
      const next = (segment + 1) % segments;
      indices.push(current + segment, nextRing + segment, current + next);
      indices.push(current + next, nextRing + segment, nextRing + next);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

export function lunarCraterProfile(t: number, depth: number): number {
  const normalizedBowl = Math.min(t / 0.835, 1);
  const bowl = -depth * Math.pow(Math.max(0, 1 - normalizedBowl * normalizedBowl), 1.35);
  const rim = depth * 0.035 * Math.exp(-Math.pow((t - 0.86) / 0.027, 2));
  return bowl + rim;
}

export function lunarCraterSurfaceShade(t: number): number {
  if (t < 0.34) return THREE.MathUtils.lerp(0.38, 0.34, t / 0.34);
  if (t < 0.8) return THREE.MathUtils.lerp(0.34, 0.46, (t - 0.34) / 0.46);
  if (t < 0.84) return THREE.MathUtils.lerp(0.46, 0.69, (t - 0.8) / 0.04);
  if (t < 0.875) return THREE.MathUtils.lerp(0.71, 0.65, (t - 0.84) / 0.035);
  return THREE.MathUtils.lerp(0.68, 0.84, (t - 0.875) / 0.125);
}
